import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const CHART = ["#4a6b53", "#c86a53", "#d9a05b", "#4a7a8c", "#7e907a", "#e4c094"];

const AXIS_TICK_NUM = { fontSize: 11, fontFamily: "IBM Plex Mono", fill: "#575752" };
const AXIS_TICK_CAT = { fontSize: 12, fill: "#575752" };
const LEGEND_STYLE = { fontFamily: "IBM Plex Sans", fontSize: 12 };
const BAR_RADIUS = [6, 6, 0, 0];
const LINE_DOT = { r: 3, fill: CHART[0] };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-[#e5e5e0] bg-white px-3 py-2 shadow-[0_2px_8px_rgba(28,28,25,0.06)]">
      {label && <div className="overline mb-1">{label}</div>}
      {payload.map((p, i) => (
        <div key={p.dataKey || p.name || i} className="flex items-center gap-2 text-xs">
          <span
            className="h-2 w-2 rounded-full"
            style={{ backgroundColor: p.color || p.fill }}
          />
          <span className="text-[#1c1c19]">{p.name}:</span>
          <span className="font-mono">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function ReportsPage() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/reports/summary").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="overline">Loading reports…</div>;

  return (
    <div className="space-y-8" data-testid="reports-page">
      <header>
        <div className="overline">Insights</div>
        <h1 className="mt-2 font-display text-4xl font-bold tracking-tight">
          Reports
        </h1>
      </header>

      <section className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="rounded-lg border border-[#e5e5e0] bg-white p-6 lg:col-span-8">
          <div className="overline">Attendance trend</div>
          <h3 className="mt-1 font-display text-lg font-bold">Last 7 days</h3>
          <div className="mt-6 h-[280px]">
            <ResponsiveContainer>
              <LineChart data={data.attendance_trend}>
                <CartesianGrid stroke="#efefe9" strokeDasharray="0" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={AXIS_TICK_NUM}
                  tickFormatter={(d) => d.slice(5)}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={AXIS_TICK_NUM}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="present"
                  name="Present"
                  stroke={CHART[0]}
                  strokeWidth={2}
                  dot={LINE_DOT}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-[#e5e5e0] bg-white p-6 lg:col-span-4">
          <div className="overline">Leave breakdown</div>
          <h3 className="mt-1 font-display text-lg font-bold">By type</h3>
          <div className="mt-4 h-[280px]">
            {data.by_leave_type.length ? (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={data.by_leave_type}
                    dataKey="count"
                    nameKey="leave_type"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={2}
                  >
                    {data.by_leave_type.map((entry, i) => (
                      <Cell key={entry.leave_type} fill={CHART[i % CHART.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={LEGEND_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-[#575752]">
                No leave data yet.
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-[#e5e5e0] bg-white p-6 lg:col-span-12">
          <div className="overline">Headcount</div>
          <h3 className="mt-1 font-display text-lg font-bold">By department</h3>
          <div className="mt-6 h-[300px]">
            <ResponsiveContainer>
              <BarChart data={data.by_department}>
                <CartesianGrid stroke="#efefe9" strokeDasharray="0" vertical={false} />
                <XAxis
                  dataKey="department"
                  tick={AXIS_TICK_CAT}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={AXIS_TICK_NUM}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="People" radius={BAR_RADIUS}>
                  {data.by_department.map((entry, i) => (
                    <Cell key={entry.department} fill={CHART[i % CHART.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>
    </div>
  );
}
